import { Atom, ShieldAlert, AlertOctagon, FlaskConical, MapPin, Building2 } from "lucide-react";

const COMPANY = {
  name: "AnagaHealth",
  address: "803/P, Padmanagar phase-II, Quthbullapur, Hyderabad-500067",
};

export default function About() {
  return (
    <div className="bg-white min-h-screen" data-testid="about-page">
      <section className="border-b-2 border-zinc-950">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-600 mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-zinc-950" /> About · Chem/Safety
          </div>
          <h1 className="font-heading font-black text-5xl md:text-6xl lg:text-7xl leading-[0.85] tracking-tighter uppercase">
            Built for<br />
            <span className="bg-zinc-950 text-white px-2 inline-block">the lab floor.</span>
          </h1>
          <p className="mt-6 text-lg text-zinc-700 max-w-xl">
            A complete <strong>laboratory health &amp; safety reference</strong> covering every element on the periodic table, published by <strong>{COMPANY.name}</strong>.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-12 gap-6">
        <Card icon={<Atom strokeWidth={2.5} />} title="118 Elements">
          Every element on the periodic table, from hydrogen to oganesson — including overview, common uses, exposure
          routes, and workplace exposure limits.
        </Card>
        <Card icon={<AlertOctagon strokeWidth={2.5} />} title="Hazard Classification">
          Five-tier color-coded hazard system (extreme · high · warning · low · unknown) lets you scan the
          periodic table by danger level at a glance.
        </Card>
        <Card icon={<FlaskConical strokeWidth={2.5} />} title="Health Symptoms">
          Each element documents acute and chronic symptoms, complications, precautions and first-aid
          treatment based on industrial/medical literature.
        </Card>
        <Card icon={<ShieldAlert strokeWidth={2.5} />} title="30 Safety Posters">
          Print-ready industrial safety posters covering radiation, corrosives, flammables, biohazards,
          confined spaces and more — designed for shop floor display.
        </Card>

        {/* Company block */}
        <div className="md:col-span-12 border-2 border-zinc-950 bg-zinc-950 text-white p-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-start" data-testid="company-block">
          <div className="md:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFD700] mb-3 flex items-center gap-2">
              <Building2 size={14} strokeWidth={2.5} /> Published by
            </div>
            <h2 className="font-heading font-black text-4xl md:text-5xl leading-none tracking-tighter uppercase">
              {COMPANY.name}
            </h2>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="border-2 border-white p-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFD700] mb-2 flex items-center gap-1.5">
                <MapPin size={12} strokeWidth={2.75} /> Registered Address
              </div>
              <div className="text-sm leading-relaxed" data-testid="company-address">
                803/P, Padmanagar phase-II,<br />
                Quthbullapur,<br />
                Hyderabad-500067
              </div>
            </div>
            <div className="border-2 border-white p-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFD700] mb-2">About AnagaHealth</div>
              <div className="text-sm leading-relaxed text-zinc-200">
                AnagaHealth produces evidence-based laboratory health &amp; safety reference materials for
                educators, lab technicians, EHS officers and chemistry students across India.
              </div>
            </div>
          </div>
        </div>

        <div className="md:col-span-12 bg-[#FFD700] border-2 border-zinc-950 p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] mb-2">Disclaimer</div>
          <p className="text-zinc-950 leading-relaxed">
            This application is a training and reference tool only. In the event of an actual chemical
            exposure, contact your local poison control center and emergency medical services immediately.
            Always consult the manufacturer&apos;s Safety Data Sheet (SDS) for the specific chemical in use.
          </p>
        </div>
      </section>
    </div>
  );
}

const Card = ({ icon, title, children }) => (
  <div className="md:col-span-6 border-2 border-zinc-950 p-6 bg-white flex gap-4 hover:neo-shadow transition-all">
    <div className="w-12 h-12 border-2 border-zinc-950 bg-zinc-950 text-white flex items-center justify-center shrink-0">
      {icon}
    </div>
    <div>
      <h3 className="font-heading font-bold text-2xl tracking-tight uppercase mb-2">{title}</h3>
      <p className="text-zinc-700 leading-relaxed">{children}</p>
    </div>
  </div>
);

export const Footer = () => (
  <footer className="border-t-2 border-zinc-950 bg-zinc-950 text-white mt-10 print:hidden" data-testid="site-footer">
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em]">
        © {new Date().getFullYear()} · CHEM/SAFETY v1
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em]">
        Prepared by <span className="text-[#FFD700] font-bold">{COMPANY.name}</span>
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
        {COMPANY.address}
      </div>
    </div>
  </footer>
);
